/**
 * Check if a string is empty, null, undefined, or contains only whitespace.
 *
 * @param value - The value to check
 * @returns true if the string is empty, null, undefined, or whitespace-only
 */
export function isStringEmpty(value: unknown): boolean {
  return !value || typeof value !== 'string' || value.trim() === ''
}

/**
 * Check if a string is NOT empty (has actual content after trimming).
 *
 * @param value - The value to check
 * @returns true if the string has content after trimming
 */
export function isStringNotEmpty(value: unknown): boolean {
  return !isStringEmpty(value)
}

import MarkdownIt from 'markdown-it'

// Configure markdown-it for help text rendering
// - html: false to prevent XSS (user input should not contain raw HTML)
// - breaks: true to convert \n to <br>
// - linkify: true to auto-convert URLs to links
const md = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: true
})

// Override link renderer to add target="_blank" and security attributes
const defaultLinkRender =
  md.renderer.rules.link_open ||
  function (tokens, idx, options, _env, self) {
    return self.renderToken(tokens, idx, options)
  }

md.renderer.rules.link_open = function (tokens, idx, options, env, self) {
  tokens[idx].attrSet('target', '_blank')
  tokens[idx].attrSet('rel', 'noopener noreferrer')
  tokens[idx].attrSet('class', 'text-purple-600 underline hover:text-purple-800')
  return defaultLinkRender(tokens, idx, options, env, self)
}

/**
 * Convert plain text with URLs and markdown-style links to HTML with clickable links.
 *
 * Supports:
 * - Plain URLs: https://example.com or http://example.com
 * - Markdown-style links: [link text](https://example.com)
 *
 * All links open in a new tab with rel="noopener noreferrer" for security.
 *
 * @param text - The plain text that may contain URLs or markdown links
 * @returns HTML string with clickable links
 */
export function linkifyText(text: string): string {
  if (!text) return ''
  return md.renderInline(text)
}
